/**
 * HomeSalvia — Visão Geral do Admin Master Sálvia
 * 
 * KPIs: Vidas Sob Gestão, Canais Ativos, ISP (Índice de Saúde da Plataforma)
 * Gráficos: Funil do Ecossistema, Maturidade dos Canais
 */
import SalviaLayout from "@/components/SalviaLayout";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Users, Building2, Activity } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { GaugeChart, FunnelChart, MaturityChart, VariationBadge, InsightsCard, IndicatorManualModal } from "@/components/home";

export default function HomeSalvia() {
  usePageTitle("Visão Geral — Administração Insight NR1");

  const { data, isLoading, error } = trpc.homeOverview.salvia.overview.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const { data: insightsData } = trpc.dashboardsAdv.insights.byProfile.useQuery(
    { profile: "salvia" },
    { refetchOnWindowFocus: false }
  );

  if (isLoading) {
    return (
      <SalviaLayout title="Visão Geral — Administração Insight NR1">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6"><Skeleton className="h-4 w-24 mb-3" /><Skeleton className="h-10 w-20" /></Card>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6"><Skeleton className="h-4 w-48 mb-4" /><Skeleton className="h-[200px]" /></Card>
            <Card className="p-6"><Skeleton className="h-4 w-48 mb-4" /><Skeleton className="h-[200px]" /></Card>
          </div>
        </div>
      </SalviaLayout>
    );
  }

  if (error) {
    return (
      <SalviaLayout title="Visão Geral — Administração Insight NR1">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <h2 className="text-lg font-semibold mb-2">Erro ao carregar dashboard</h2>
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  if (!data) return null;

  const { kpis, funnel, maturity } = data;

  return (
    <SalviaLayout title="Visão Geral — Administração Insight NR1">
      <div className="space-y-6">
        {/* ─── 3 KPI Cards ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Vidas Sob Gestão */}
          <Card className="p-5 border-l-4" style={{ borderLeftColor: "#002F59" }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Vidas Sob Gestão</p>
                <p className="text-3xl font-bold text-[#002F59] mt-1">
                  {kpis.vidasSobGestao.value.toLocaleString("pt-BR")}
                </p>
                <div className="mt-2">
                  <VariationBadge value={kpis.vidasSobGestao.variacao} tipo="percentual" suffix="vs mês anterior" />
                </div>
              </div>
              <div className="p-3 rounded-full bg-[#002F59]/10">
                <Users size={24} className="text-[#002F59]" />
              </div>
            </div>
          </Card>

          {/* Canais Ativos */}
          <Card className="p-5 border-l-4" style={{ borderLeftColor: "#0D7377" }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Canais Ativos</p>
                <p className="text-3xl font-bold text-[#0D7377] mt-1">
                  {kpis.canaisAtivos.value}
                </p>
                <div className="mt-2">
                  <VariationBadge value={kpis.canaisAtivos.variacao} tipo="absoluto" suffix="vs mês anterior" />
                </div>
              </div>
              <div className="p-3 rounded-full bg-[#0D7377]/10">
                <Building2 size={24} className="text-[#0D7377]" />
              </div>
            </div>
          </Card>

          {/* ISP — Índice de Saúde da Plataforma */}
          <Card className="p-5 border-l-4" style={{ borderLeftColor: "#C0FF00" }}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Saúde da Plataforma</p>
                <GaugeChart
                  value={kpis.isp.value}
                  label=""
                  size={140}
                  ranges={[
                    { min: 0, max: 40, color: "#DC2626", label: "Crítico" },
                    { min: 40, max: 70, color: "#D97706", label: "Atenção" },
                    { min: 70, max: 100, color: "#16A34A", label: "Saudável" },
                  ]}
                />
              </div>
              <div className="p-3 rounded-full bg-[#C0FF00]/20">
                <Activity size={24} className="text-[#002F59]" />
              </div>
            </div>
          </Card>
        </div>

        {/* ─── ISP Breakdown ─── */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-[#002F59] mb-3">Composição do Índice de Saúde (ISP)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Cobertura de Importação", value: kpis.isp.components.coberturaImportacao },
              { label: "Progresso de Coleta", value: kpis.isp.components.progressoColeta },
              { label: "Avaliações Concluídas", value: kpis.isp.components.taxaAvaliacao },
              { label: "Documentos Gerados", value: kpis.isp.components.taxaDocumentos },
            ].map((comp, i) => (
              <div key={i} className="text-center">
                <div className="relative w-16 h-16 mx-auto mb-2">
                  <svg className="w-16 h-16" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="26" fill="none" stroke="#E5E7EB" strokeWidth="6" />
                    <circle
                      cx="32" cy="32" r="26" fill="none"
                      stroke={comp.value >= 70 ? "#16A34A" : comp.value >= 40 ? "#D97706" : "#DC2626"}
                      strokeWidth="6" strokeLinecap="round"
                      strokeDasharray={`${(comp.value / 100) * 163.36} 163.36`}
                      transform="rotate(-90 32 32)"
                    />
                    <text x="32" y="36" textAnchor="middle" className="font-bold" style={{ fontSize: "14px", fill: "#002F59" }}>
                      {comp.value}%
                    </text>
                  </svg>
                </div>
                <p className="text-xs text-muted-foreground">{comp.label}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* ─── Gráficos: Funil + Maturidade ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <FunnelChart data={funnel} title="Funil do Ecossistema" />
          </Card>
          <Card className="p-6">
            <MaturityChart data={maturity} title="Maturidade dos Canais" />
          </Card>
        </div>

        {/* ─── Insights Operacionais ─── */}
        {insightsData?.insights && insightsData.insights.length > 0 && (
          <Card className="p-6">
            <InsightsCard insights={insightsData.insights as any[]} title="Insights Operacionais" />
          </Card>
        )}
      </div>
      <IndicatorManualModal profile="salvia" />
    </SalviaLayout>
  );
}
